<template>
  <Modal
    fullbelowsm
    :title="!editData ? $t('projects.email_providers.create') : $t('projects.email_providers.edit')"
    :primary-button="$t('save')"
    :secondary-button="$t('cancel')"
    :secondary-button-disabled="loading"
    :primary-button-loading="loading"
    :primary-button-disabled="!provider"
    @primary="onSaveClick"
    @secondary="onCancelClick"
    @opened="onModalOpen"
  >
    <div class="w-600 flex flex-col">
      <div class="w-full flex flex-col justify-start mt-3 py-3 px-10 flex-shrink-0">
        <ValidationObserver ref="validationObserver" v-slot="{ errors }" class="w-full">
          <FormLabel :label="$t('projects.email_providers.provider')" />
          <div class="font-500">{{ providerName }}</div>
          <template v-if="provider === 'AWS_SES'">
            <FormInput v-model="config.key_id" :label="$t('projects.email_providers.key_id')" name="key_id" :error="errors.key_id" rules="required" class="mt-2" />
            <FormInput v-model="config.key" :label="$t('projects.email_providers.key')" name="key" :error="errors.key" rules="required" class="mt-2" />
            <FormSelect
              v-model="config.region"
              :options="awsRegions"
              default-reducer
              :label="$t('projects.email_providers.region')"
              name="region"
              :error="errors.region"
              rules="required"
              class="mt-2"
              append-to-body
            />
          </template>
          <template v-else-if="provider === 'SENDGRID'">
            <FormInput v-model="config.key" :label="$t('projects.email_providers.api_key')" name="api_key" :error="errors.api_key" rules="required" class="mt-2" />
          </template>
        </ValidationObserver>
      </div>
    </div>

    <template #footer>
      <div class="w-full flex py-4 px-10">
        <PillButton v-if="editData" error :text="$t('delete')" :disabled="loading" @click="onDeleteClick" />
        <PillButton outlined :text="$t('cancel')" :disabled="loading" class="ml-auto" @click="onCancelClick" />
        <PillButton primary :loading="loading" :disabled="loading" class="ml-2" :text="$t('save')" outlined @click="onSaveClick" />
      </div>
    </template>
  </Modal>
</template>

<script>
import { mapActions, mapState } from 'vuex';

export default {
  name: 'CreateEmailProviderModal',
  props: {
    projectId: {
      type: String,
      required: true,
    },
    provider: {
      type: String,
    },
    providerName: {
      type: String,
    },
    editData: {
      type: Object,
    },
    onDelete: {
      type: Function,
    },
  },
  data() {
    return {
      loading: false,
      config: {},
      awsRegions: [
        { label: 'af-south-1', value: 'af-south-1' },
        { label: 'ap-east-1', value: 'ap-east-1' },
        { label: 'ap-northeast-1', value: 'ap-northeast-1' },
        { label: 'ap-northeast-2', value: 'ap-northeast-2' },
        { label: 'ap-northeast-3', value: 'ap-northeast-3' },
        { label: 'ap-south-1', value: 'ap-south-1' },
        { label: 'ap-south-2', value: 'ap-south-2' },
        { label: 'ap-southeast-1', value: 'ap-southeast-1' },
        { label: 'ap-southeast-2', value: 'ap-southeast-2' },
        { label: 'ap-southeast-3', value: 'ap-southeast-3' },
        { label: 'ap-southeast-4', value: 'ap-southeast-4' },
        { label: 'ap-southeast-5', value: 'ap-southeast-5' },
        { label: 'ap-southeast-7', value: 'ap-southeast-7' },
        { label: 'ca-central-1', value: 'ca-central-1' },
        { label: 'ca-west-1', value: 'ca-west-1' },
        { label: 'eu-central-1', value: 'eu-central-1' },
        { label: 'eu-central-2', value: 'eu-central-2' },
        { label: 'eu-north-1', value: 'eu-north-1' },
        { label: 'eu-south-1', value: 'eu-south-1' },
        { label: 'eu-south-2', value: 'eu-south-2' },
        { label: 'eu-west-1', value: 'eu-west-1' },
        { label: 'eu-west-2', value: 'eu-west-2' },
        { label: 'eu-west-3', value: 'eu-west-3' },
        { label: 'il-central-1', value: 'il-central-1' },
        { label: 'me-central-1', value: 'me-central-1' },
        { label: 'me-south-1', value: 'me-south-1' },
        { label: 'mx-central-1', value: 'mx-central-1' },
        { label: 'sa-east-1', value: 'sa-east-1' },
        { label: 'us-east-1', value: 'us-east-1' },
        { label: 'us-east-2', value: 'us-east-2' },
        { label: 'us-gov-east-1', value: 'us-gov-east-1' },
        { label: 'us-gov-west-1', value: 'us-gov-west-1' },
        { label: 'us-west-1', value: 'us-west-1' },
        { label: 'us-west-2', value: 'us-west-2' },
      ],
    };
  },
  created() {
    this.config = this.getDefaultConfig(this.provider);
    if (this.editData) {
      this.config = structuredClone(this.editData?.config || {});
    }
  },
  computed: {
    ...mapState(['availableEmailProviders']),
    providers() {
      return Object.keys(this.availableEmailProviders).map((key) => ({
        value: key,
        label: this.availableEmailProviders[key].name,
      }));
    },
  },
  methods: {
    ...mapActions(['showToastMessage', 'createProjectEmailProvider', 'updateProjectEmailProvider']),
    getDefaultConfig(provider) {
      const config = {
        AWS_SES: {
          enabled: true,
          key_id: '',
          key: '',
          region: '',
        },
        SENDGRID: {
          enabled: true,
          key: '',
        },
      };

      return config?.[provider] || {};
    },
    onModalOpen() {
      this.$refs?.name?.focus();
    },
    async onDeleteClick() {
      if (this.onDelete) {
        await this.onDelete();
      }
    },
    async onSaveClick() {
      const valid = await this.$refs.validationObserver.validate();
      if (!valid.valid) return;
      this.loading = true;
      if (this.editData) {
        const response = await this.updateProjectEmailProvider({
          project_id: this.projectId,
          provider: this.provider,
          config: this.config,
        });
        if (response) {
          this.$emit('close');
        }
      } else {
        const response = await this.createProjectEmailProvider({
          project_id: this.projectId,
          provider: this.provider,
          config: this.config,
        });
        if (response) {
          this.$emit('close');
        }
      }
      this.loading = false;
    },
    onCancelClick() {
      this.$emit('close');
    },
  },
};
</script>
